<template>
  <PageLayout class="custom-surveys-page mb">
    <template slot="header">
      <div class="header-wrap m-3 mx-lg-0">
        <breadcrumb :list="breadcrumbList" />
        <b-button
          v-if="$can('create', 'customSurvey')"
          variant="tertiary"
          class="medium btn-custom-survey-create"
          :to="{ name: 'custom-surveys.create' }"
        >
          <i class="material-icons">add_circle</i>
          {{ labels.create }}
        </b-button>
      </div>
    </template>
    <template slot="main">
      <b-card class="mb-4">
        <header slot="header">
          <b-row class="pt-1 pb-3">
            <b-col cols="12" xl="6">
              <b-form-group>
                <b-input-group>
                  <b-form-input
                    autocomplete="off"
                    v-model="query.title"
                    class="text-form-group-btn"
                    v-on:keyup.13="loadCustomSurveysList"
                  />
                  <label class="custom-input-label">
                    {{ labels.list.searchTitle }}
                  </label>
                  <template v-slot:append>
                    <b-button
                      class="apply-form-group-btn"
                      variant="primary"
                      size="lg"
                      @click="loadCustomSurveysList"
                    >
                      <i class="material-icons">search</i>
                    </b-button>
                  </template>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col cols="12" xl="3">
              <b-form-group v-if="!isUser">
                <b-form-select
                  class="custom-survey-form-input status-select clickable"
                  :class="query.status"
                  :value="query.status"
                  required
                  @input="changeStatus($event)"
                >
                  <option :value="STATUS.ACTIVE">
                    {{ labels.list.status[STATUS.ACTIVE] }}
                  </option>
                  <option :value="STATUS.SCHEDULED">
                    {{ labels.list.status[STATUS.SCHEDULED] }}
                  </option>
                  <option
                    v-if="$can('create', 'customSurvey')"
                    :value="STATUS.DRAFT"
                  >
                    {{ labels.list.status[STATUS.DRAFT] }}
                  </option>
                  <option
                    v-if="$can('create', 'customSurvey')"
                    :value="STATUS.TEMPLATE"
                  >
                    {{ labels.list.status[STATUS.TEMPLATE] }}
                  </option>
                  <option :value="STATUS.CLOSED">
                    {{ labels.list.status[STATUS.CLOSED] }}
                  </option>
                </b-form-select>
                <label class="custom-input-label">
                  {{ labels.list.status.label }}
                </label>
              </b-form-group>
            </b-col>
          </b-row>
          <div class="text-gray03">
            <h6 v-if="!loading">
              {{ customSurveysCount }}
              {{
                customSurveysCount === 1
                  ? labels.list.statusDescriptionSingle[query.status]
                  : labels.list.statusDescription[query.status]
              }}
            </h6>
          </div>
        </header>
        <div v-if="!loading && !customSurveys.length" class="empty-labels">
          <img :src="'/home/empty-state-custom-survey.svg'" />
          <p>{{ labels.list.empty.title }}</p>
          <small>{{ labels.list.empty.message }}</small>
        </div>
        <section v-if="loading" class="loading-content">
          <loading />
          <p>{{ labels.loading }}</p>
        </section>
        <section v-if="!loading && customSurveys.length > 0">
          <section class="survey-table">
            <div class="row" v-for="survey in customSurveys" :key="survey._id">
              <div class="col text-primary">
                <i
                  v-if="survey.isTemplate"
                  class="material-icons template-icon"
                  :title="labels.list.status['template']"
                >
                  bookmark
                </i>
                {{
                  survey.title[languageSelected] ||
                    survey.title[survey.availableLanguages[0]] ||
                    labels.list.untitled
                }}
              </div>
              <div class="col">
                <div class="wrapper-row">
                  <div class="internal-row">
                    <div class="col">
                      <div class="col">
                        <small class="bold mr-2">
                          {{ labels.list.creator }}:
                        </small>
                        {{ survey.creator }}
                      </div>
                      <div
                        v-if="
                          [STATUS.ACTIVE, STATUS.SCHEDULED].includes(
                            query.status
                          )
                        "
                        class="col"
                      >
                        <small class="bold mr-2">
                          {{
                            query.status === STATUS.ACTIVE
                              ? labels.list.nextSurveyDate
                              : labels.list.launch
                          }}:
                        </small>
                        {{
                          survey.nextDeliveySurveyDate
                            ? $moment(survey.nextDeliveySurveyDate).format(
                                'DD/MM/YYYY - HH:00'
                              )
                            : '--'
                        }}
                      </div>
                      <div
                        v-if="
                          [STATUS.ACTIVE, STATUS.SCHEDULED].includes(
                            query.status
                          )
                        "
                        class="col"
                      >
                        <small class="bold mr-2">
                          {{ labels.list.closure }}:
                        </small>
                        {{
                          survey.launchConfiguration.endDate
                            ? $moment(
                                survey.launchConfiguration.endDate
                              ).format('DD/MM/YYYY')
                            : '--'
                        }}
                      </div>
                    </div>
                    <div class="col actions">
                      <b-link
                        router-tag="button"
                        v-if="
                          [STATUS.ACTIVE].includes(survey.status) &&
                            survey.isResponder
                        "
                        :to="{
                          name: 'custom-surveys.survey.begin',
                          params: { id: survey._id },
                        }"
                        class="btn text-primary btn-link"
                      >
                        <i
                          class="material-icons"
                          :title="labels.list.actions.survey"
                        >
                          event_note
                        </i>
                        {{ labels.list.actions.survey }}
                      </b-link>
                      <b-button
                        variant="link"
                        v-if="
                          STATUS.DRAFT === survey.status &&
                            $can('create', 'customSurvey') &&
                            !isUser
                        "
                        :title="labels.list.actions.edit"
                        class="text-quartenary"
                        @click="editCustomSurvey(survey._id)"
                      >
                        <i class="material-icons">create</i>
                        {{ labels.list.actions.edit }}
                      </b-button>
                      <b-button
                        variant="link"
                        v-if="
                          [
                            STATUS.ACTIVE,
                            STATUS.SCHEDULED,
                            STATUS.CLOSED,
                          ].includes(survey.status) &&
                            !isUser &&
                            survey.canAccess
                        "
                        :title="labels.list.actions.reports"
                        class="text-success"
                        @click="linkToReports(survey)"
                      >
                        <i class="material-icons">pie_chart</i>
                        {{ labels.list.actions.reports }}
                      </b-button>

                      <b-button
                        variant="link"
                        v-if="
                          $can('create', 'customSurvey') &&
                            !isUser &&
                            (survey.isOwner ||
                              isAdmin ||
                              survey.status === STATUS.TEMPLATE)
                        "
                        :title="
                          survey.status === STATUS.TEMPLATE
                            ? labels.list.actions.use
                            : labels.list.actions.duplicate
                        "
                        :disabled="survey.loadingDuplicate"
                        class="text-info"
                        @click="duplicateCustomSurvey(survey)"
                      >
                        <i
                          v-if="survey.loadingDuplicate"
                          class="mdi mdi-loading mdi-spin"
                        >
                        </i>
                        <i v-else class="material-icons">
                          content_copy
                        </i>
                        {{
                          survey.status === STATUS.TEMPLATE
                            ? labels.list.actions.use
                            : labels.list.actions.duplicate
                        }}
                      </b-button>

                      <b-button
                        variant="link"
                        v-if="
                          $can('create', 'customSurvey') &&
                            [STATUS.ACTIVE, STATUS.SCHEDULED].includes(
                              survey.status
                            ) &&
                            !isUser &&
                            (survey.isOwner || isAdmin)
                        "
                        class="text-danger"
                        :title="labels.list.actions.archive"
                        :disabled="survey.loadingClose"
                        @click="closeCustomSurvey(survey)"
                      >
                        <i
                          v-if="survey.loadingClose"
                          class="mdi mdi-loading mdi-spin"
                        >
                        </i>
                        <i v-if="!survey.loadingClose" class="material-icons">
                          archive
                        </i>
                        {{ labels.list.actions.archive }}
                      </b-button>

                      <b-button
                        variant="link"
                        v-if="
                          $can('create', 'customSurvey') &&
                            [STATUS.DRAFT, STATUS.CLOSED].includes(
                              survey.status
                            ) &&
                            !isUser &&
                            (survey.isOwner || isAdmin)
                        "
                        class="text-danger"
                        :title="labels.list.actions.delete"
                        :disabled="survey.loadingRemove"
                        @click="removeCustomSurvey(survey)"
                      >
                        <i
                          v-if="survey.loadingRemove"
                          class="mdi mdi-loading mdi-spin"
                        >
                        </i>
                        <i v-if="!survey.loadingRemove" class="material-icons">
                          delete_forever
                        </i>
                        {{ labels.list.actions.delete }}
                      </b-button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <b-pagination
            v-if="customSurveysCount > query.perPage"
            v-model="query.page"
            :total-rows="customSurveysCount"
            :per-page="query.perPage"
            align="center"
            class="mt-4"
            @input="loadCustomSurveysList()"
          >
          </b-pagination>
        </section>
      </b-card>
      <modal-plans
        ref="component-modal-plans"
        :title="labels.title"
        :hideHeaderClose="true"
        :visible="true"
        v-if="!$can('use', 'customSurvey') || isBasicPlan"
        @hide="handleHide"
      />
    </template>
  </PageLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import PageLayout from '@/components/Home/PageLayout.vue'
import Breadcrumb from '@/components/Shared/Breadcrumb.vue'
import Loading from '@/components/Shared/Loading.vue'
import { isUser, isAdmin, isBasicPlan } from '@/service/user'
import ModalPlans from '@/components/Modal/ModalPlans.vue'

const STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  CLOSED: 'closed',
  SCHEDULED: 'scheduled',
  TEMPLATE: 'template',
}

export default {
  props: ['status'],
  components: {
    PageLayout,
    Breadcrumb,
    Loading,
    ModalPlans,
  },
  data() {
    return {
      loading: false,
      query: {
        page: 1,
        perPage: 10,
        status: STATUS.ACTIVE,
        title: '',
      },
      STATUS,
    }
  },
  computed: {
    ...mapGetters({
      resources: 'resources',
      languageSelected: 'languageSelected',
      customSurveys: 'customSurveys/customSurveys',
      customSurveysCount: 'customSurveys/customSurveysCount',
    }),
    labels() {
      return this.resources.page.home.customSurveys
    },
    breadcrumbList() {
      return [
        {
          label: this.resources.page.home.customSurveys.title,
        },
      ]
    },
    isUser() {
      return isUser()
    },
    isAdmin() {
      return isAdmin()
    },
    isBasicPlan() {
      return isBasicPlan()
    },
  },
  async mounted() {
    const status = this.status
    if (status) {
      this.query.status = status
    }
    this.$root.$emit('setPageTitle', { context: this, path: 'custom-surveys' })
    await this.loadCustomSurveysList()
  },
  methods: {
    linkToReports(survey) {
      const groupId = undefined
      this.$store.dispatch('customSurveyResults/updateSelectedGroupId', groupId)
      this.$router.push({
        name: 'custom-surveys.reports',
        params: { id: survey._id },
      })
    },
    async loadCustomSurveysList() {
      this.loading = true
      await this.$store.dispatch('customSurveys/loadCustomSurveysList', {
        offset: this.query.perPage * (this.query.page - 1),
        limit: this.query.perPage,
        status: this.query.status,
        title: this.query.title,
      })
      this.loading = false
    },
    editCustomSurvey(id) {
      this.$router.push({ name: 'custom-surveys.create', params: { id } })
    },
    async closeCustomSurvey(survey) {
      await this.$store.dispatch('customSurveys/closeCustomSurvey', survey._id)
      await this.loadCustomSurveysList()
    },
    async removeCustomSurvey(survey) {
      await this.$store.dispatch('customSurveys/removeCustomSurvey', survey._id)
      await this.loadCustomSurveysList()
    },
    async duplicateCustomSurvey(survey) {
      await this.$store.dispatch(
        'customSurveys/duplicateCustomSurvey',
        survey._id
      )
      this.query.status = STATUS.DRAFT
      await this.loadCustomSurveysList()
    },
    async changeStatus(status) {
      this.query.status = status
      await this.loadCustomSurveysList()
    },
    handleHide() {
      this.$router.push({ name: 'dashboard' })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/styles/custom/variables';
@import '~@/styles/custom/colors';

.custom-surveys-page {
  height: 100%;

  .header-wrap {
    display: flex;
    justify-content: space-between;

    .btn-custom-survey-create {
      border-radius: 10px !important;
      height: $btn-height;
      padding: 0.7rem 1rem 0.5rem;

      i {
        display: inline-block;
        font-size: 16px;
        line-height: 16px;
        vertical-align: text-top;
      }
    }
  }

  .text-form-group-btn {
    border-top-left-radius: 10px !important;
    border-bottom-left-radius: 10px !important;
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    background-color: map-get($theme-colors, 'white') !important;
    border-color: var(--gray07) !important;
    height: 45px !important;
  }

  .apply-form-group-btn {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    border-top-right-radius: 10px !important;
    border-bottom-right-radius: 10px !important;
    height: 45px !important;
  }

  .status-select {
    font-weight: bold;
    color: map-get($theme-colors, 'white') !important;
    &.draft {
      background: map-get($theme-colors, 'quartenary') !important;
    }
    &.scheduled {
      background: map-get($theme-colors, 'purple00') !important;
    }
    &.active {
      background: map-get($theme-colors, 'green-dark') !important;
    }
    &.closed {
      background: map-get($theme-colors, 'secondary') !important;
    }
    &.template {
      background: map-get($theme-colors, 'blue') !important;
    }
    option {
      background: map-get($theme-colors, 'white') !important;
      color: map-get($theme-colors, 'primary') !important;
    }
  }

  main {
    .custom-survey-form-input {
      border-radius: 10px !important;
      background-color: map-get($theme-colors, 'white') !important;
      border-color: var(--gray07) !important;
    }

    .card {
      .card-body {
        padding: 0px !important;

        .empty-labels {
          text-align: center;
          padding: 3rem 0;
          max-width: 800px;
          margin: 0 auto;

          @media (max-width: 425px) {
            text-align: center;
          }

          p {
            font-size: 38px;
            max-width: 80%;
            margin: 0 auto;

            @media (max-width: 768px) {
              max-width: initial;
            }

            @media (max-width: 425px) {
              font-size: 18px;
            }
          }

          small {
            font-size: 22px;
            max-width: 80%;
            margin: 10px auto;
            display: inline-block;

            @media (max-width: 425px) {
              font-size: 12px;
              max-width: initial;
            }
          }
        }

        .loading-content {
          text-align: center;
          width: 100%;
          margin-top: 10%;
          margin-bottom: 10%;
          margin-left: 0.9px;
          margin-right: 0.9px;
        }

        .survey-table {
          .row {
            display: flex;
            flex-direction: column;
            align-items: stretch;

            background: map-get($theme-colors, 'light');
            color: map-get($theme-colors, 'gray04');
            padding: 12.5px 0;
            margin: 0px !important;

            &:nth-child(odd) {
              background: map-get($theme-colors, 'white');
            }

            .col {
              .badge {
                &.draft {
                  background: map-get($theme-colors, 'quartenary');
                }
                &.scheduled {
                  background: map-get($theme-colors, 'purple00');
                }
                &.active {
                  background: map-get($theme-colors, 'green-dark');
                }
                &.closed {
                  background: map-get($theme-colors, 'secondary');
                }
              }

              .template-icon {
                color: map-get($theme-colors, 'blue');
              }

              .wrapper-row {
                .internal-row {
                  display: flex;
                  flex-direction: row;
                  padding: 12.5px 0;

                  .actions {
                    flex-grow: 1.3 !important;
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
